import React, { useEffect, useState } from 'react';
import { FileText, Plus, Pencil, Trash2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const Editor = ({ tpl, onClose, onSaved }) => {
  const [f, setF] = useState({ title: tpl?.title || '', message: tpl?.message || '' });
  const save = async () => {
    try {
      if (tpl?.id) await api.put(`/api/urgent-alert-templates/${tpl.id}`, f);
      else await api.post('/api/urgent-alert-templates', f);
      onSaved();
      onClose();
    } catch (e) { toast({ title: 'Could not save template', description: e.response?.data?.detail?.[0]?.msg, variant: 'destructive' }); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="urgent-template-editor">
        <DialogHeader><DialogTitle>{tpl?.id ? 'Edit template' : 'New template'}</DialogTitle></DialogHeader>
        <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="urgent-template-title" />
        <Textarea rows={4} placeholder="Message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="urgent-template-message" />
        <DialogFooter><Button onClick={save} disabled={f.title.trim().length < 2 || f.message.trim().length < 2} data-testid="urgent-template-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Tap a template → confirm → sent to everyone (two taps). "Edit first" fills the form instead.
export const AlertTemplates = ({ onUse, onQuickSent }) => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/api/urgent-alert-templates').then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const quickSend = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/urgent-alerts', { title: pick.title, message: pick.message, audience: 'all' });
      toast({ title: 'Urgent alert sent', description: `Delivered to ${r.data.total} staff` });
      onQuickSent();
      setPick(null);
    } catch (e) { toast({ title: 'Could not send', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  const del = async (t) => { if (window.confirm(`Delete template "${t.title}"?`)) { await api.delete(`/api/urgent-alert-templates/${t.id}`); load(); } };

  return (
    <div className="space-y-2" data-testid="urgent-templates">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-600"><FileText className="h-3.5 w-3.5" /> Templates — tap to send to everyone</p>
        <button onClick={() => setEdit({})} className="flex items-center gap-1 text-xs text-red-600" data-testid="urgent-template-add"><Plus className="h-3.5 w-3.5" /> New template</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((t) => (
          <span key={t.id} className="group inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 pl-3 pr-1 py-1 text-xs text-red-800">
            <button onClick={() => setPick(t)} className="font-medium" data-testid={`urgent-template-${t.title}`}>{t.title}</button>
            <button onClick={() => setEdit(t)} className="rounded-full p-1 text-red-400 hover:bg-red-100" data-testid={`urgent-template-edit-${t.title}`}><Pencil className="h-3 w-3" /></button>
            <button onClick={() => del(t)} className="rounded-full p-1 text-red-400 hover:bg-red-100" data-testid={`urgent-template-delete-${t.title}`}><Trash2 className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      {edit && <Editor tpl={edit} onClose={() => setEdit(null)} onSaved={load} />}
      <Dialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent data-testid="urgent-template-confirm">
          <DialogHeader><DialogTitle>Send "{pick?.title}" to everyone?</DialogTitle></DialogHeader>
          <p className="whitespace-pre-line rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{pick?.message}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { onUse(pick); setPick(null); }} data-testid="urgent-template-edit-first">Edit first / choose staff</Button>
            <Button onClick={quickSend} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="urgent-template-send"><Send className="h-4 w-4 mr-1" /> Send now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
